import React, { useRef, useState, useEffect } from 'react';
import { Modal, Button, FormGroup, ControlLabel, FormControl, Checkbox } from 'react-bootstrap';
import PropTypes from 'prop-types';

import Spinner from '../../../../components/Loaders/Spinner';
import Alert from '../../../../components/Alert';

import { getUserForPayment } from '../../../../services/endpoints/payments';

const AddPaymentModal = ({ open, error, isLoading, isLoadingSubmit, methods, excludedMethods, onHide, onSubmit }) => {
  const formNode = useRef(null);
  const usernamePrev = useRef('');

  const [modalError, setError] = useState(error);
  const [searchUser, setSearchUser] = useState(null);
  const [showReferrer, setShowReferrer] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(false);

  const onSubmitForm = () => {
    if (disableSubmit) return;
    const current = formNode?.current;
    const formData = {
      username: current?.username?.value,
      amount: current?.amount?.value,
      method: current?.method?.value,
      memo: current?.memo?.value,
      include_referral_payment: showReferrer ? !!current?.include_referral_payment?.checked : false,
    };
    onSubmit?.(formData, 'add');
  };

  const getUser = async (username) => {
    setDisableSubmit(true);
    usernamePrev.current = username;

    try {
      const response = await getUserForPayment({ username });
      setDisableSubmit(false);
      setError('');
      if (response.success) {
        setSearchUser(response?.data);
      }
    } catch (error) {
      setDisableSubmit(true);
      setError(error?.data?.error_message ?? 'Something went wrong');
    }
  };

  const onBlurUsername = () => {
    const current = formNode?.current;
    if (current?.username?.value && usernamePrev.current !== current?.username?.value) {
      getUser(current?.username?.value);
    }
  };

  const onEnterUsername = (e) => {
    if (e.keyCode === 13) {
      e.preventDefault();
      const current = formNode?.current;
      if (current?.username?.value && usernamePrev.current !== current?.username?.value) {
        getUser(current?.username?.value);
      }
    }
  };

  const onShow = () => {
    setError('');
  };

  const onChangeMethod = (e) => {
    if (excludedMethods?.includes(Number(e.target.value)) && searchUser?.referrer) {
      setShowReferrer(false);
    } else if (searchUser?.referrer) {
      setShowReferrer(true);
    }
  };

  useEffect(() => {
    if (!excludedMethods?.includes(Number(formNode?.current?.method?.value)) && searchUser?.referrer) {
      setShowReferrer(true);
    } else {
      setShowReferrer(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchUser]);

  useEffect(() => {
    setError(error);
  }, [error]);

  return (
    <Modal show={open} onHide={onHide} onShow={onShow} backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Add payment</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {modalError && <Alert text={modalError} />}
        {isLoading && <div className="modal-loader-small" />}

        <form ref={formNode}>
          <FormGroup>
            <ControlLabel>Username</ControlLabel>
            <FormControl type="text" name="username" onBlur={onBlurUsername} onKeyDown={onEnterUsername} />
          </FormGroup>
          <FormGroup>
            <ControlLabel>Amount</ControlLabel>
            <FormControl type="text" name="amount" />
          </FormGroup>
          <FormGroup>
            <ControlLabel>Method</ControlLabel>
            <FormControl componentClass="select" name="method" onChange={onChangeMethod}>
              {methods?.map((method) => (
                <option key={method.id} value={method.id}>
                  {method.title}
                </option>
              ))}
            </FormControl>
          </FormGroup>
          <FormGroup>
            <ControlLabel>Memo</ControlLabel>
            <FormControl type="text" name="memo" />
          </FormGroup>
          {showReferrer && (
            <FormGroup>
              <Checkbox className="mb-0" name="include_referral_payment" defaultChecked>
                Take in affiliate commission
              </Checkbox>
            </FormGroup>
          )}
        </form>
      </Modal.Body>
      <Modal.Footer className="custom__modal-footer">
        <Button bsStyle="primary" type="submit" disabled={isLoadingSubmit || disableSubmit} onClick={onSubmitForm}>
          {isLoadingSubmit && <Spinner />}
          <span>Add payment</span>
        </Button>
        <Button
          onClick={() => {
            onHide();
          }}
        >
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

AddPaymentModal.propTypes = {
  error: PropTypes.string,
  open: PropTypes.bool,
  isLoading: PropTypes.bool,
  isLoadingSubmit: PropTypes.bool,
  methods: PropTypes.array,
  excludedMethods: PropTypes.array,
  onHide: PropTypes.func,
  onSubmit: PropTypes.func,
};

export default AddPaymentModal;
